import React, {ReactNode, useMemo} from 'react';

import {useStore} from 'effector-react';
import {useTranslation} from 'react-i18next';
import {useHistory, useRouteMatch} from 'react-router-dom';

import {PillItem} from 'components/ui-kit/Pills';
import {PERMISSION_ALIASES} from 'core/constants/permissionAliases';
import {ROUTE} from 'core/routes';
import {isCustomsRole$, permissions$, profileState$} from 'models/auth';

import {UserPermission, UserPermissionValue} from 'types';

import {CustomPills, CustomTitle, NavigationContainer, Heading} from './styled';

type Props = {
    headingContent?: ReactNode;
};

export const ProfileNavigationTabs = ({headingContent}: Props) => {
    const {t} = useTranslation();
    const history = useHistory();
    const {path} = useRouteMatch();
    const state = useStore(profileState$);
    const isCustomsRole = useStore(isCustomsRole$);
    const permissions = useStore(permissions$);
    const canViewOrganizations = permissions.includes(
        PERMISSION_ALIASES.registeredTurnoverParticipantsView,
    );

    const items: Array<PillItem & {route: string; permissions?: UserPermissionValue[]}> = useMemo(
        () => [
            {
                label: t('pages.profile.pills.CommonData'),
                cb: () => history.push(ROUTE.PROFILE),
                route: ROUTE.PROFILE,
            },
            {
                label: t('pages.profile.pills.Factories'),
                cb: () => history.push(ROUTE.FACTORIES),
                route: ROUTE.FACTORIES,
                permissions: [UserPermission.FACTORY_GRID_VIEW],
            },
            ...(isCustomsRole
                ? [
                      {
                          label: t('pages.profile.pills.Staff'),
                          cb: () => history.push(ROUTE.CUSTOMS_USERS_LIST),
                          route: ROUTE.CUSTOMS_USERS_LIST,
                      },
                  ]
                : [
                      {
                          label: t('pages.profile.pills.Staff'),
                          cb: () => history.push(ROUTE.STAFF),
                          route: ROUTE.STAFF,
                      },
                  ]),
            {
                label: t('pages.profile.pills.Storages'),
                cb: () => history.push(ROUTE.STORAGES),
                route: ROUTE.STORAGES,
                permissions: [
                    PERMISSION_ALIASES.allStoragesView,
                    PERMISSION_ALIASES.userOwnStoragesView,
                ],
            },
            {
                label: t('pages.profile.pills.Billing'),
                cb: () => history.push(ROUTE.BILLING),
                route: ROUTE.BILLING,
                permissions: [UserPermission.FACTORY_BILLING_VIEW],
            },
            {
                label: t('pages.profile.pills.Customs'),
                cb: () => history.push(ROUTE.CUSTOMS),
                route: ROUTE.CUSTOMS,
                permissions: [UserPermission.CUSTOMS_LIST],
            },
            ...(canViewOrganizations
                ? [
                      {
                          label: t('pages.profile.pills.Organizations'),
                          cb: () => history.push(ROUTE.ORGANIZATIONS),
                          route: ROUTE.ORGANIZATIONS,
                      },
                  ]
                : []),
        ],
        [t, isCustomsRole, canViewOrganizations],
    );

    const pathIndex = () => {
        if (path === ROUTE.BILLING_INVOICES) {
            return items.findIndex((item) => item.route === ROUTE.BILLING);
        }

        return items.findIndex((item) => item.route === path);
    };

    return (
        <NavigationContainer>
            <Heading>
                <CustomTitle size={4}>{state.profile?.legalPerson?.company_name || ''}</CustomTitle>
                {headingContent}
            </Heading>
            <CustomPills forceIndex={pathIndex()} items={items} />
        </NavigationContainer>
    );
};
